import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, FolderTree } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Category {
  id: string;
  name: string;
  slug: string;
  type: string;
  parentCategoryId: string | null;
  description: string | null;
  isActive: boolean;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function CategoriesSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "", slug: "", type: "main" as "main" | "sub",
    parentCategoryId: "", description: "", isActive: true,
  });

  const { data: mainCategories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories", { type: "main", includeInactive: "true" }],
    queryFn: () => api.get<Category[]>(`/categories?type=main&includeInactive=true`, sessionId),
  });

  const { data: subCategories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories", { type: "sub", includeInactive: "true" }],
    queryFn: () => api.get<Category[]>(`/categories?type=sub&includeInactive=true`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: typeof form) => {
      const body = {
        name: payload.name,
        slug: payload.slug || generateSlug(payload.name),
        type: payload.type,
        parentCategoryId: payload.type === "sub" ? payload.parentCategoryId : null,
        description: payload.description || null,
        isActive: payload.isActive,
      };
      return editingId
        ? api.patch(`/categories/${editingId}`, body, sessionId)
        : api.post(`/categories`, body, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/categories"] });
      setDialogOpen(false);
      setEditingId(null);
      setForm({ name: "", slug: "", type: "main", parentCategoryId: "", description: "", isActive: true });
      toast({ title: editingId ? "Category updated" : "Category created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/categories/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/categories"] });
      toast({ title: "Category deleted" });
    },
  });

  const handleEdit = (cat: Category) => {
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      slug: cat.slug,
      type: cat.type as "main" | "sub",
      parentCategoryId: cat.parentCategoryId || "",
      description: cat.description || "",
      isActive: cat.isActive,
    });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({ name: "", slug: "", type: "main", parentCategoryId: "", description: "", isActive: true });
    setDialogOpen(true);
  };

  const renderList = (items: Category[], title: string) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FolderTree className="h-4 w-4" /> {title} ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No categories yet.</p>
        ) : (
          <div className="space-y-2">
            {items.map((cat) => (
              <div
                key={cat.id}
                className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                data-testid={`category-row-${cat.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{cat.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">{cat.slug}</span>
                    {!cat.isActive && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                  </div>
                  {cat.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(cat)}>Edit</Button>
                  <Button
                    variant="ghost" size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteMutation.mutate(cat.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Categories"
        description="Product categories and subcategories"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-category">
              <Plus className="h-4 w-4 mr-1" /> New Category
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {renderList(mainCategories, "Main Categories")}
        {renderList(subCategories, "Sub Categories")}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Category" : "New Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Type</label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v as "main" | "sub" })}
                disabled={!!editingId}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">Main Category</SelectItem>
                  <SelectItem value="sub">Sub Category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.type === "sub" && (
              <div>
                <label className="text-sm font-medium mb-1 block">Parent Category</label>
                <Select
                  value={form.parentCategoryId}
                  onValueChange={(v) => setForm({ ...form, parentCategoryId: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select parent" /></SelectTrigger>
                  <SelectContent>
                    {mainCategories.map((mc) => (
                      <SelectItem key={mc.id} value={mc.id}>{mc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setForm({
                    ...form,
                    name,
                    slug: editingId ? form.slug : generateSlug(name),
                  });
                }}
                data-testid="input-category-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Slug *</label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                data-testid="input-category-slug"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate(form)}
                disabled={!form.name || saveMutation.isPending || (form.type === "sub" && !form.parentCategoryId)}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
